export type UserSettingsResponse = {
  defaultSystemPrompt: string;
  importantInfo: string;
  defaultModelName: string;
  defaultTemperature?: number;
  defaultContextLength?: string | null;
  defaultWhisperLanguage?: string | null;
  defaultEnableSyntaxHighlighting?: boolean;
  defaultShowFullThinking?: boolean;
  defaultThinkMode?: string;
  defaultTools?: string[];
  theme: 'light' | 'dark' | 'blonde';
  showTokenUsage?: boolean;
  showFollowUps?: boolean;
  showArchive?: boolean;
  telegramApiId?: number | null;
  telegramApiHash?: string | null;
  telegramSessionString?: string | null;
  hasPushSubscription?: boolean;
  userMemoryEnabled?: boolean;
  chatMemoryEnabled?: boolean;
  defaultEnabledSkills?: string[];
}

// Tool progress types for streaming
export type ToolProgressSearchResult = {
  title: string;
  link: string;
  snippet: string;
  favicon?: string;
};

export type ToolProgressSiteProcessing = {
  title: string;
  link: string;
  favicon?: string;
  status: 'processing' | 'completed' | 'failed';
};

export type WhisperWord = {
  word: string;
  p: number;         // probability 0–1
  t0?: number | null; // ms from audio start
  t1?: number | null;
};

export type ToolProgressUpdate = {
  type: 'search_results' | 'site_processing' | 'status' | 'done' | 'error' | 'text_chunk' | 'thinking_chunk' | 'word_tokens';
  searchResults?: ToolProgressSearchResult[];
  siteProcessing?: ToolProgressSiteProcessing;
  status?: string;
  text?: string;
  thinking?: string;        // for thinking_chunk: a chunk of sub-agent internal reasoning
  resetThinking?: boolean;  // when true (on status): clear accumulated thinkingText
  wordTokens?: WhisperWord[];
  error?: string;
  // Final result fields
  toolContent?: string;
  assistantContent?: string;
  assistantMessage?: string;
  toolMessageId?: string;
  assistantMessageId?: string;
  assistantAttachmentIds?: string[];
  isError?: boolean;
  timeTakenMs?: number;
  sources?: MessageSource[];
};

// MCP Tool approval types
export type McpToolAllowMode = 'allow' | 'ask';

export type ToolApprovalInfo = {
  needsApproval: boolean;
  mcpServerId?: string;
  serverName?: string;
};

export type ToolApprovalDecision = 'deny' | 'allow' | 'always-allow';

export type AttachmentType = 'image' | 'document' | 'text' | 'pdf' | 'video' | 'audio';

export type MessageSource = {
  title: string;
  link: string;
  favicon?: string;
};

export type AttachmentMeta = {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  isImage: boolean;
  imageWidth?: number | null;
  imageHeight?: number | null;
  textContent?: string | null; // Preview of text content (truncated)
  type: AttachmentType;
};

export function getAttachmentType(contentType: string, filename: string): AttachmentType {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('audio/')) return 'audio';
  if (contentType.startsWith('video/') || contentType === 'video/mp4') return 'video';
  const lowerFilename = filename.toLowerCase();
  if (lowerFilename.endsWith('.mp3') || lowerFilename.endsWith('.ogg') || lowerFilename.endsWith('.wav') ||
      lowerFilename.endsWith('.m4a') || lowerFilename.endsWith('.flac') || lowerFilename.endsWith('.aac')) return 'audio';
  if (lowerFilename.endsWith('.mp4') || lowerFilename.endsWith('.webm') || lowerFilename.endsWith('.mov')) return 'video';
  if (contentType === 'application/pdf' || lowerFilename.endsWith('.pdf')) return 'pdf';
  
  // Consider many file types as text
  const textExtensions = [
    '.txt', '.md', '.csv', '.log',
    '.js', '.ts', '.jsx', '.tsx', '.json',
    '.py', '.java', '.cpp', '.c', '.h', '.hpp', '.cs', '.go', '.rb', '.php', '.swift', '.kt',
    '.html', '.css', '.scss', '.xml', '.svg',
    '.yaml', '.yml', '.toml', '.ini', '.cfg',
    '.sql', '.sh', '.bat', '.ps1'
  ];
  
  if (contentType === 'text/plain' || textExtensions.some(ext => lowerFilename.endsWith(ext))) {
    return 'text';
  }
  
  return 'document';
}

export function getAttachmentIcon(contentType: string, filename: string): string {
  const lowerFilename = filename.toLowerCase();
  if (contentType === 'application/pdf' || lowerFilename.endsWith('.pdf')) return '\uD83D\uDCC4'; // 📄
  if (contentType.includes('word') || lowerFilename.endsWith('.doc') || lowerFilename.endsWith('.docx')) return '\uD83D\uDCC3'; // 📃
  if (contentType === 'text/plain' || lowerFilename.endsWith('.txt')) return '\uD83D\uDCDD'; // 📝
  if (lowerFilename.endsWith('.md')) return '\uD83D\uDCDD'; // 📝
  if (lowerFilename.endsWith('.csv')) return '\uD83D\uDCCA'; // 📊
  return '\uD83D\uDCCE'; // 📎
}

/**
 * Get the appropriate icon component name for a file attachment
 * Returns the component that should be rendered for this file type
 */
export function getAttachmentIconComponent(contentType: string, filename: string): 
  'pdf' | 'doc' | 'docx' | 'txt' | 'csv' | 'html' | 'svg' | 'sql' | 'log' | 'generic' {
  const lowerFilename = filename.toLowerCase();
  
  // PDF files
  if (contentType === 'application/pdf' || lowerFilename.endsWith('.pdf')) return 'pdf';
  
  // Word documents
  if (contentType.includes('word') || lowerFilename.endsWith('.doc')) return 'doc';
  if (lowerFilename.endsWith('.docx')) return 'docx';
  
  // Specific file types with dedicated icons
  if (lowerFilename.endsWith('.html') || lowerFilename.endsWith('.htm')) return 'html';
  if (lowerFilename.endsWith('.svg')) return 'svg';
  if (lowerFilename.endsWith('.sql')) return 'sql';
  if (lowerFilename.endsWith('.log')) return 'log';
  if (lowerFilename.endsWith('.csv')) return 'csv';
  if (lowerFilename.endsWith('.txt')) return 'txt';
  
  // All other text-based files use generic icon
  return 'generic';
}

export type ToolCall = {
  id: string;       // Unique identifier for this tool call
  name: string;     // Tool function name
  arguments: string; // JSON string of arguments
};

export type Message = {
  id: string;
  timestamp: number; // Timestamp of the message
  order?: number; // Order of the message in the chat, legacy support, can be removed later
  parentId?: string; // ID of the parent message, if any
  children?: Message[] | null;
  parent?: Message | null; // Parent message object, if any
  role: 'user' | 'assistant' | 'system' | 'tool' | 'root' | 'context';
  content: string;
  thinking?: string;
  toolsFunctionName?: string; // Legacy: Name of the first/only tool function called
  toolsFunctionArguments?: string; // Legacy: JSON string of the first/only tool function arguments
  toolCalls?: ToolCall[]; // New: all tool calls from this message (multi-call support)
  toolName?: string; // For tool role messages: name of the tool that produced this result
  toolCallId?: string; // For tool role messages: id of the matching tool_call in the preceding assistant message
  contentTruncated?: boolean; // For tool role messages: full content not sent; lazy-load via /api/messages/:id/content
  contentLength?: number;     // Actual full content length when truncated
  images?: string[]; // Base64 encoded images, deprecated
  attachmentIds?: string[]; // IDs of attachments (images, documents, text fragments)
  attachmentMetas?: AttachmentMeta[]; // Pre-loaded attachment metadata (dimensions, etc.) from the server
  isAborted?: boolean; // For aborted messages
  errorMessage?: string | null; // For error messages
  modelName?: string; // Model that was used for the message
  tokensIn?: number; // Number of tokens used in the message
  tokensOut?: number; // Number of tokens generated in the response
  timeTaken?: number; // Time taken to generate the response
  thinkingTimeTaken?: number | null; // Time taken for thinking, if applicable
  collapsedMessages?: Message[]; // Collapsed messages under this message

  /** Stub: placeholder for a non-active branch; full branch loads on demand when navigated to */
  isStub?: true;

  // Model loading progress
  modelLoadCompleted?: number; // Model loading progress in bytes
  modelLoadTotal?: number; // Total model size in bytes
  promptEvalCompleted?: number; // Prompt evaluation progress (tokens)
  promptEvalTotal?: number; // Total prompt tokens
  
  // Waiting for worker progress
  waitingInfo?: string;

  // Tool progress
  toolProgress?: {
    operationId?: string;
    status?: string;
    streamingText?: string;
    thinkingText?: string;
    wordTokens?: WhisperWord[];
    searchResults?: ToolProgressSearchResult[];
    sitesProcessing?: ToolProgressSiteProcessing[];
  };

  /** Word-level probability data from whisper transcription (persisted after tool completes) */
  whisperWords?: WhisperWord[];
  /** ID of the whisper-data attachment for this transcription */
  whisperDataAttachmentId?: string;

  // MCP Tool approval
  pendingToolApproval?: {
    toolName: string;
    serverName: string;
    arguments: string;
  };
  toolApprovalDecision?: ToolApprovalDecision;

  // Transient client-side field: pending document File objects before they are encoded to base64
  _documentFiles?: File[];

  /** Timestamp of the last token applied — used for merge on restore */
  lastTokenTimestamp?: number;

  /** Buffered tokens with timestamps — used to merge cached content with live WS data */
  _tokenBuffer?: { ts: number; content: string; thinking: string }[];

  /** Client-computed start time for live generation timer (Date.now() - elapsed_ms from first chunk) */
  _fetchStartMs?: number;
  /** True once _fetchStartMs has been refined with the server's elapsed_ms on the first token */
  _fetchStartRefined?: boolean;
  /** Client timestamp when first thinking token arrived — used for live thinking timer */
  _thinkingStartMs?: number;
  /** Accumulated time (ms) from prior iterations in this turn (earlier LLM calls + tool execution) */
  _precedingMs?: number;

  // Sources from web search / fetch_page tools (shown in UI below assistant responses)
  sources?: MessageSource[];

  // Context compaction fields (only for role === 'context')
  previousMessageIds?: string[];   // IDs of messages kept verbatim after the summary
  compactedMessageIds?: string[];  // IDs of messages that were summarized (for UI display)
};

export type ChatResponse = {
  chats: ChatResponseItem[];
  hasMore?: boolean;
  /** true when the response contains only chats changed since the requested timestamp */
  partial?: boolean;
}

export type GalleryItem = {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  isImage: boolean;
  imageWidth?: number | null;
  imageHeight?: number | null;
  chatId: string;
  createdAt: string;
};

export type GalleryResponse = {
  items: GalleryItem[];
  total: number;
  hasMore: boolean;
};

export type MessageResponse = {
  title: string;
  messages: Message[];
  createdAt: number;
  createdBy: string;
  createdByPicture: string | null;
  createdByEmailMd5: string | null;
  enableSyntaxHighlighting?: boolean;
  showFullThinking?: boolean;
  /** In-progress assistant message from server memory (present when generation is active) */
  runningMessage?: {
    messageId: string;
    content: string;
    thinking: string;
    modelName: string;
    lastTokenTimestamp?: number;
    toolCalls?: { id: string; name: string; arguments: string }[];
    modelLoadCompleted?: number;
    modelLoadTotal?: number;
    promptEvalCompleted?: number;
    promptEvalTotal?: number;
    waitingInfo?: string;
  };
  runningPhase?: string;
  runningToolProgress?: { toolName: string; operationId: string } | null;
}

export type ChatResponseItemSettings = {
  id?: string;

  startFromBeginning?: boolean;

  temperature?: number;
  thinkMode?: string;
  whisperLanguage?: string;
  enableSyntaxHighlighting?: boolean;
  showFullThinking?: boolean;
  contextLength?: string | null;
  predictLength?: string | null;
  model?: string | null;

  tools?: string[] | null; // Names of the tools used in this chat
  enabledSkills?: string[]; // IDs of enabled skills for this chat
}

export type ChatResponseItem = ChatResponseItemSettings & {
  id: string;
  model: string;
  modifiedAt: number;
  createdAt: number;
  name: string;
  keywords: string[];
  isNameGenerated: boolean;
  isArchived?: boolean;
  isFavorite?: boolean;

  // schedule fields
  isScheduled?: boolean;
  cron?: string | null;
  hasUnreadMessages?: boolean;
}

export type OllamaStreamResponse = {
  message?: {
    content?: string;
    thinking?: string;
    tool_calls?: { id?: string; function: { name: string; arguments: Record<string, string | number | boolean> } }[];
  };
  done?: boolean;
  total_duration?: number;
  load_duration?: number;
  prompt_eval_count?: number;
  prompt_eval_duration?: number;
  eval_count?: number;
  eval_duration?: number;
  error?: string;

  // Model loading progress
  model_load_completed?: number; // in bytes
  model_load_total?: number; // in bytes
  prompt_eval_completed?: number;
  prompt_eval_total?: number;
  
  // Waiting for worker progress
  waiting_info?: string;
};

export type OllamaMessage = {
  id: string;
  order?: number; // Order of the message in the chat, -1 for system message
  role: 'user' | 'assistant' | 'system' | 'tool' | 'root' | 'context';
  content: string;
  images?: string[];
  attachmentIds?: string[];
  documents?: string[]; // Base64 encoded document content for new uploads
  thinking?: string;
  tool_calls?: { id?: string; function: { name: string; arguments: Record<string, string | number | boolean> } }[];
  tool_name?: string; // For tool role messages: name of the tool that produced this result
  tool_call_id?: string; // For tool role messages: id of the matching tool_call in the preceding assistant message
};

export type LoadedModel = {
  name: string;
  server: string;
  size: number;
  size_vram: number;
  context_length: number;
  expires_at: string;
}

export type Model = {
  name: string;
  server: string;
  capabilities: string[];
  performanceLevel: 'full' | 'partial';
  parameter_size: string;
  quantization_level: string;
  family: string;
  size: number;
  context_length: number;

  isFavorite?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  recommendedContextSize?: number | null;
  displayName?: string | null;

  model_creator?: string;
  artificial_analysis_intelligence_index?: number;
  artificial_analysis_coding_index?: number;
}

export type UserData = { role: string; tokensLeft: number; picture?: string; name?: string; givenName?: string; familyName?: string; email: string; emailMd5: string; id: string };

export type ServerPayloadTypeResponse = 'ollama' | 'comfyui';

export type PayloadTaskInfoResponse = {
  name: string;
  status: 'running' | 'pending' | 'stopping' | 'warning';
  payload: ServerPayloadTypeResponse;
  sizeMb?: number;
  sizeVramMb?: number;
  contextLength?: number;
  details?: string;
}

export type ServerPayload = {
  id: string;
  isBackground: boolean; // whether this payload runs if other payloads are active
  type: ServerPayloadTypeResponse;
  model: string;
  description: string;
  sizeGb: number | null;
  contextSize: number | null; // for ollama context length to know if we can run task in parallel with existing
};

export type ServiceStatusResponse = {
  name: string;
  version: string;
  ramMemoryUsageMb?: number;
  vramMemoryUsageMb?: number;
  tasks: PayloadTaskInfoResponse[];
  error?: string;
  totalRamMb?: number;
  totalVramMb?: number;
}

type PayloadInfoResponse = {
  id: string;
  type: ServerPayloadTypeResponse;
  model: string;
  description: string;
  sizeGb: number | null;
  contextSize: number | null;
  isBackground: boolean;  
};

type QueueItemResponse = {
  payload: PayloadInfoResponse;
  user: UserData;
}

export type ServerPayloadStatusResponse = {
  serverKey: string;
  hardMemoryLimitGb?: number | null;

  // service status
  services: ServiceStatusResponse[];

  // internal state
  loadedModels: {  
    type: ServerPayloadTypeResponse;
    model: string;
    sizeGb: number | null;
    contextSize: number | null;
  }[];
  runningTasks: QueueItemResponse[];
  queue: QueueItemResponse[];

  // system stats
  systemStats?: SystemStatsResponse | null;
}

export type SystemStatsResponse = {
  uptime: number;
  cpu: {
    usage: number;
  };
  memory: {
    total: number;
    free: number;
    used: number;
  };
  disk: {
    free: number;
    size: number;
    used: number;
    readBytesPerSec: number;
    writeBytesPerSec: number;
    active: number;
  };
  network: {
    inBytesPerSec: number;
    outBytesPerSec: number;
  };
  gpu?: {
    usage: number;
    memoryTotal: number;
    memoryUsed: number;
    memoryFree: number;
    temperature: number;
    powerConsumption: number;
  };
};

export const legacyToActualMap: Record<string, string> = {
  getWeatherByLocation: 'get_weather',
  getWeather: 'get_weather',
  getContentByUrl: 'fetch_page',
  getSearchResults: 'web_search',
  generateSdxlImage: 'generate_sdxl_image',
  generateZImageImage: 'generate_z_image_image',
  generateFluxImage: 'generate_flux_image',
  generateImage: 'generate_image',
  updateImage: 'update_image',
  ocrImage: 'ocr_image'
};

export type VRamLogEntryResponse = {
  _id: string;
  timestamp: string;
  userEmail: string;
  serverKey: string;
  modelRequested: string;
  memoryNeededGb: number | null;
  memoryAvailableGb: number;
  loadedModelsBefore: { model: string; sizeGb: number }[];
  modelAffected: string;
  modelAffectedSizeGb: number | null;
  contextLength: number | null;
  action: 'Load' | 'Unload';
};